import { useEffect } from 'react'
import type {
  BogglePosition,
  BoggleResetOptions,
  BoggleRotationDirection,
  BoggleSkillMode,
  BoggleSnapshot,
} from '~/boggle/engine/standard/types'
import type { BogglePlaygroundSettings } from '~/boggle/config/playground'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import {
  createWoggleDebugBridge,
  type WoggleDebugBridge,
} from '~/boggle/ui/state/woggleDebug'
import { preloadPlayableWordLexiconForDev } from '~/boggle/ui/state/playableWordLexiconDevLoader'

export type BoggleDebugBridge = {
  advance(ms: number): BoggleSnapshot
  altClickSwap(position: BogglePosition): BoggleSnapshot
  clearSkillSelection(): BoggleSnapshot
  copyTile(position: BogglePosition): BoggleSnapshot
  ctrlClickCopy(position: BogglePosition): BoggleSnapshot
  getSnapshot(): BoggleSnapshot
  getPaused(): boolean
  pause(): void
  removeLastSelectionEntry(): BoggleSnapshot
  renderGameToText(): string
  reset(options?: BoggleResetOptions): BoggleSnapshot
  resume(): void
  rotateBoard(direction: BoggleRotationDirection): BoggleSnapshot
  rotateLeft(): BoggleSnapshot
  rotateRight(): BoggleSnapshot
  tapSelection(position: BogglePosition): BoggleSnapshot
  setPlaygroundSettings(settings: BogglePlaygroundSettings): BoggleSnapshot
  swapTile(position: BogglePosition): BoggleSnapshot
  submitPath(path: BogglePosition[]): BoggleSnapshot
  togglePaused(): void
  toggleSkill(mode: BoggleSkillMode): BoggleSnapshot
  useActiveSkillOnTile(position: BogglePosition): BoggleSnapshot
  zapTile(position: BogglePosition): BoggleSnapshot
}

declare global {
  interface Window {
    boggleDebug?: BoggleDebugBridge
    woggleDebug?: WoggleDebugBridge
  }
}

export function useBoggleBrowserBridge(store: BoggleStore) {
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return
    }

    let cancelled = false

    void (async () => {
      try {
        await preloadPlayableWordLexiconForDev()
      } catch (error) {
        console.error(error)
        return
      }
      if (cancelled) {
        return
      }

      // Keep Boggle hooks namespaced so this route never overwrites Caver's globals.
      window.boggleDebug = {
        advance: ms => store.advance(ms),
        altClickSwap: position => store.altClickSwap(position),
        clearSkillSelection: () => {
          store.clearSkillSelection()
          return store.getSnapshot()
        },
        copyTile: position => store.copyTile(position),
        ctrlClickCopy: position => store.ctrlClickCopy(position),
        getSnapshot: () => store.getSnapshot(),
        getPaused: () => store.getPaused(),
        pause: () => {
          store.pause()
        },
        removeLastSelectionEntry: () => store.removeLastSelectionEntry(),
        renderGameToText: () => store.renderGameToText(),
        reset: (options = {}) => store.reset(options),
        resume: () => {
          store.resume()
        },
        rotateBoard: direction => store.rotateBoard(direction),
        rotateLeft: () => store.rotateBoard('left'),
        rotateRight: () => store.rotateBoard('right'),
        tapSelection: position => store.tapSelection(position),
        setPlaygroundSettings: settings => store.setPlaygroundSettings(settings),
        swapTile: position => store.swapTile(position),
        submitPath: path => store.submitPath(path),
        togglePaused: () => {
          store.togglePaused()
        },
        toggleSkill: mode => store.toggleSkill(mode),
        useActiveSkillOnTile: position => store.useActiveSkillOnTile(position),
        zapTile: position => store.zapTile(position),
      }
      window.woggleDebug = createWoggleDebugBridge(store)
    })()

    return () => {
      cancelled = true
      delete window.boggleDebug
      delete window.woggleDebug
    }
  }, [store])
}
